import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useColdCallAuth } from '../context/ColdCallAuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Phone, 
  Target, 
  TrendingUp, 
  Users, 
  Award,
  ArrowRight,
  Loader2,
  Eye,
  EyeOff,
  Mic,
  BarChart3,
  Zap,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/coldcall`;

export default function ColdCallLabLanding() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useColdCallAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/cold-call-lab/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      toast.success(response.data.message);
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        login(response.data.token, response.data.user);
        toast.success('Welcome back!');
        navigate('/cold-call-lab/dashboard');
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        login(response.data.token, response.data.user);
        toast.success('Registration successful! Awaiting admin approval.', {
          duration: 5000
        });
        navigate('/cold-call-lab/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Mic className="h-6 w-6" />,
      title: "Voice-Only Practice",
      description: "No text shortcuts - train your actual cold calling voice skills"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Real Buyer Personas",
      description: "Practice with AI personas that push back like real prospects"
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Track Progress",
      description: "Monitor your improvement across categories and scenarios"
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Earn Achievements",
      description: "Unlock badges as you master different personas and categories"
    }
  ];

  const personas = [
    { name: "Skeptical CEO", difficulty: "Hard", color: "from-red-500 to-rose-500" },
    { name: "Busy VP Sales", difficulty: "Medium", color: "from-amber-500 to-orange-500" },
    { name: "Curious Manager", difficulty: "Easy", color: "from-green-500 to-emerald-500" },
    { name: "Executive Assistant", difficulty: "Medium", color: "from-amber-500 to-orange-500" },
    { name: "Technical Buyer", difficulty: "Hard", color: "from-red-500 to-rose-500" },
    { name: "Friendly Founder", difficulty: "Easy", color: "from-green-500 to-emerald-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 rounded-lg">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">HFS Cold Call Lab</h1>
              <p className="text-xs text-slate-400">by HireForSkillz</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-400 hover:text-white text-sm transition-colors">
              Back to HFS
            </Link>
            <Button 
              onClick={() => setShowAuth(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 px-4 py-2 rounded-full mb-6">
            <Zap className="h-4 w-4 text-cyan-500" />
            <span className="text-cyan-500 text-sm font-medium">AI-Powered Training</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Master The Art of
            <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent"> Cold Calling</span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            Train with AI-powered buyer simulations. Get brutally honest feedback. 
            Become <span className="text-white font-semibold">unstoppable</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => { setShowAuth(true); setIsLogin(false); }}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white h-14 px-8 text-lg"
              data-testid="start-training-btn"
            >
              Start Training Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => { setShowAuth(true); setIsLogin(true); }}
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 h-14 px-8 text-lg"
              data-testid="sign-in-btn"
            >
              Sign In
            </Button>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <span className="bg-white/5 border border-white/10 px-4 py-2 rounded-full text-sm text-slate-300">
              <Mic className="h-4 w-4 inline mr-2" />
              Voice-only practice
            </span>
            <span className="bg-white/5 border border-white/10 px-4 py-2 rounded-full text-sm text-slate-300">
              <Target className="h-4 w-4 inline mr-2" />
              Real buyer personas
            </span>
            <span className="bg-white/5 border border-white/10 px-4 py-2 rounded-full text-sm text-slate-300">
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Track progress
            </span>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Why <span className="text-cyan-500">Cold Call Lab</span>?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Personas Preview */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Practice with <span className="text-cyan-500">Real Personas</span>
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            From skeptical CEOs to friendly founders - master every type of buyer
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {personas.map((persona, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
              >
                <div className={`bg-gradient-to-r ${persona.color} w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
                  {persona.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{persona.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    persona.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                    persona.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {persona.difficulty}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-500 mb-2">7</div>
              <div className="text-slate-400 text-sm">SaaS Categories</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-500 mb-2">6</div>
              <div className="text-slate-400 text-sm">Buyer Personas</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-500 mb-2">6</div>
              <div className="text-slate-400 text-sm">Call Scenarios</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-500 mb-2">100%</div>
              <div className="text-slate-400 text-sm">Free for HFS</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to become a cold calling pro?
          </h2>
          <p className="text-slate-400 mb-8">
            Join HFS students who are sharpening their sales skills every day.
          </p>
          <Button 
            onClick={() => { setShowAuth(true); setIsLogin(false); }}
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white h-14 px-8 text-lg"
          >
            Create Free Account
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-slate-500 text-sm mt-4">100% Free for HFS Students</p>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-900 border-white/10 relative">
            <button
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              data-testid="close-auth-modal"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader>
              <CardTitle className="text-white text-center">
                {isLogin ? 'Welcome Back' : 'Join HFS Cold Call Lab'}
              </CardTitle>
              <p className="text-slate-400 text-center text-sm">
                {isLogin ? 'Sign in to continue your training' : 'Create your account to start training'}
              </p>
            </CardHeader>
            <CardContent>
              {/* Tab Switcher */}
              <div className="flex mb-6 bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    isLogin ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="sign-in-tab"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    !isLogin ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="register-tab"
                >
                  Register
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label className="text-slate-300">Full Name</Label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="John Doe"
                      data-testid="name-input"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="you@hfs.com"
                    data-testid="email-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="bg-white/10 border-white/20 text-white pr-10"
                      placeholder="••••••••"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                  disabled={loading}
                  data-testid="submit-auth-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Please wait...</>
                  ) : (
                    isLogin ? 'Sign In' : 'Create Account'
                  )}
                </Button>

                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-center text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    Forgot Password?
                  </button>
                )}

                <p className="text-center text-xs text-slate-500 mt-4">
                  100% Free for HFS Students
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-900 border-white/10 relative">
            <CardHeader>
              <CardTitle className="text-white text-center">Forgot Password</CardTitle>
              <p className="text-slate-400 text-center text-sm">
                Enter your email and we'll notify the admin to reset your password
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="you@hfs.com"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                  disabled={forgotPasswordLoading}
                >
                  {forgotPasswordLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    'Submit Request'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setShowAuth(true); }}
                  className="w-full text-center text-sm text-slate-400 hover:text-white"
                >
                  Back to Login
                </button>
              </form>

              <button
                onClick={() => setShowForgotPassword(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © 2026 HireForSkillz. Cold Call Lab - Train. Practice. Close.
          </p>
        </div>
      </footer>
    </div>
  );
}
